import { Body, Sphere, Vec3, World } from 'cannon-es';
import { Group, Mesh, MeshStandardMaterial, Vector3 } from 'three';
import type { ToolId } from '../config/toolConfig';
import { ToolState } from '../core/GameState';

export interface LaunchOptions {
  origin: Vector3;
  target: Vector3;
  power: number;
  wind: number;
}

export abstract class RescueTool {
  readonly group = new Group();
  readonly id: ToolId;
  state = ToolState.AVAILABLE;
  body: Body | null = null;
  impactSpeed = 0;
  landingPoint: Vector3 | null = null;
  protected world: World | null = null;
  protected driftSpeed = 0;

  protected constructor(id: ToolId) {
    this.id = id;
    this.group.name = `RescueTool_${id}`;
  }

  protected attachBallisticBody(world: World, mass: number, radius: number): Body {
    const body = new Body({
      mass,
      shape: new Sphere(radius),
      linearDamping: 0.02,
      angularDamping: 0.12,
    });
    world.addBody(body);
    this.world = world;
    this.body = body;
    return body;
  }

  launch(world: World, options: LaunchOptions): void {
    const body = this.attachBallisticBody(world, 0.8, 0.55);
    body.position.set(options.origin.x, options.origin.y, options.origin.z);
    const flightTime = 1.72;
    const gravity = Math.abs(world.gravity.y);
    const velocity = options.target.clone().sub(options.origin);
    velocity.x = velocity.x / flightTime + options.wind * 1.2;
    velocity.z /= flightTime;
    velocity.y = (options.target.y - options.origin.y + 0.5 * gravity * flightTime * flightTime) / flightTime;
    const strength = 0.68 + options.power * 0.56;
    body.velocity.set(velocity.x * strength, velocity.y * (0.82 + options.power * 0.25), velocity.z * strength);
    body.angularVelocity.set(3, 6, 2);
    this.group.position.copy(options.origin);
    this.state = ToolState.THROWN;
  }

  update(delta: number, riverSpeed: number): void {
    if (this.body && this.state === ToolState.THROWN) {
      this.group.position.set(this.body.position.x, this.body.position.y, this.body.position.z);
      this.group.rotation.x += this.body.angularVelocity.x * delta;
      this.group.rotation.y += this.body.angularVelocity.y * delta;
      // 水面平面位于 y=0.2，视觉模型最大厚度和碰撞球半径并不完全一致。
      // 在低帧率 WebGL 环境中提前用向下速度确认接触，避免弹体跨帧卡在“飞行中”。
      if (this.body.position.y <= 1.05 && this.body.velocity.y < 0.8) {
        this.impactSpeed = this.body.velocity.length();
        this.landingPoint = new Vector3(this.body.position.x, 0.56, this.body.position.z);
        this.group.position.copy(this.landingPoint);
        this.world?.removeBody(this.body);
        this.body = null;
        this.state = ToolState.FLOATING;
        this.driftSpeed = riverSpeed;
      }
    } else if (this.state === ToolState.FLOATING || this.state === ToolState.ATTACHED) {
      this.group.position.x += delta * this.driftSpeed * 1.25;
      this.group.position.y = 0.55 + Math.sin(performance.now() * 0.004) * 0.08;
    }
  }

  protected standardMaterial(color: number): MeshStandardMaterial {
    return new MeshStandardMaterial({ color, roughness: 0.6, metalness: 0.03, flatShading: true });
  }

  dispose(): void {
    if (this.body && this.world) this.world.removeBody(this.body);
    this.group.traverse((object) => {
      if (object instanceof Mesh) {
        object.geometry.dispose();
        if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose());
        else object.material.dispose();
      }
    });
  }
}
